import helpers from './helpers';
import api from './api';
import { metaobjectAlias, buildMetaobjectFragment } from './metaobject-helpers';

import AddressFormatter from '@shopify/address';
const addressFormatter = new AddressFormatter('en');

import GET_PRODUCTS_QUERY from '@js/graphql/2024_01/products/get.graphql';
import PRODUCT_FRAGMENT from '@js/graphql/2024_01/products/product_fragment.graphql';

// Filter ID patterns
export const FILTER_ID_PATTERNS = {
  // Product filters
  PRODUCT_CATEGORY: /^filter\.t\.category\.(.+)$/,
  PRODUCT_TAG: /^filter\.p\.tag\.(.+)$/,
  PRODUCT_TYPE: /^filter\.p\.product_type\.(.+)$/,
  PRODUCT_VENDOR: /^filter\.p\.vendor\.(.+)$/,
  PRODUCT_METAFIELD: /^filter\.p\.m\.(.+)$/,

  VARIANT_AVAILABILITY: /^filter\.v\.availability\.(.+)$/,
  VARIANT_OPTION: /^filter\.v\.option\.(.+)$/,
  VARIANT_PRICE: /^filter\.v\.price\.(.+)$/,
  VARIANT_TAXONOMY: /^filter\.v\.t\.(.+)$/,
  VARIANT_METAFIELD: /^filter\.v\.m\.(.+)$/
};

export default {
  generateProductFilterUrlParam(id, value) {
    // Product category filter
    const productCategoryMatch = id?.match(FILTER_ID_PATTERNS?.PRODUCT_CATEGORY);
    if (productCategoryMatch) {
      return `filter.filter.t.category=${encodeURIComponent(productCategoryMatch[1])}`;
    }

    // Product tag filter
    const productTagMatch = id?.match(FILTER_ID_PATTERNS?.PRODUCT_TAG);
    if (productTagMatch) {
      return `filter.filter.p.tag=${encodeURIComponent(value)}`;
    }

    // Product type filter
    const productTypeMatch = id?.match(FILTER_ID_PATTERNS?.PRODUCT_TYPE);
    if (productTypeMatch) {
      return `filter.filter.p.product_type=${encodeURIComponent(value)}`;
    }

    // Product vendor filter
    const productVendorMatch = id?.match(FILTER_ID_PATTERNS.PRODUCT_VENDOR);
    if (productVendorMatch) {
      return `filter.filter.p.vendor=${encodeURIComponent(value)}`;
    }

    // Product metafield filter
    const productMetafieldMatch = id?.match(FILTER_ID_PATTERNS.PRODUCT_METAFIELD);
    if (productMetafieldMatch) {
      const namespaceKey = productMetafieldMatch[1].replace(`.${value.toLowerCase()}`, '');

      return `filter.filter.p.m.${namespaceKey}=${encodeURIComponent(value)}`;
    }

    // Variant availability filter
    const variantAvailabilityMatch = id?.match(FILTER_ID_PATTERNS.VARIANT_AVAILABILITY);
    if (variantAvailabilityMatch) {
      return `filter.filter.v.availability=${variantAvailabilityMatch[1] === '1'}`;
    }

    // Variant option filter
    const variantOptionMatch = id?.match(FILTER_ID_PATTERNS.VARIANT_OPTION);
    if (variantOptionMatch) {
      return `filter.filter.v.option=${encodeURIComponent(value)}`;
    }

    // Variant price filter

    // Variant taxonomy filter
    const variantTaxonomyMatch = id?.match(FILTER_ID_PATTERNS.VARIANT_TAXONOMY);
    if (variantTaxonomyMatch) {
      const namespaceKeyArray = variantTaxonomyMatch[1].split('.');
      const taxonomy = namespaceKeyArray.pop();
      const namespaceKey = namespaceKeyArray.join('.');
      const taxonomyArray = taxonomy.split('-');
      const taxonomyId = taxonomyArray.pop();

      return `filter.filter.v.t.${namespaceKey}=${encodeURIComponent(`gid://shopify/TaxonomyValue/${taxonomyId}`)}`;
    }

    // Variant metafield filter
    const variantMetafieldMatch = id?.match(FILTER_ID_PATTERNS.VARIANT_METAFIELD);
    if (variantMetafieldMatch) {
      const namespaceKey = variantMetafieldMatch[1].replace(`.${value.toLowerCase()}`, '');

      return `filter.filter.v.m.${namespaceKey}=${encodeURIComponent(value)}`;
    }
  },

  generateProductFilterFromParam(param, value) {
    // Product category filter
    const productCategoryMatch = param.match(FILTER_ID_PATTERNS.PRODUCT_CATEGORY);
    if (productCategoryMatch) {
      return {
        category: {
          id: value
        }
      };
    }

    // Product tag filter
    const productTagMatch = param.match(FILTER_ID_PATTERNS.PRODUCT_TAG);
    if (productTagMatch) {
      return {
        tag: value
      };
    }

    // Product type filter
    const productTypeMatch = param.match(FILTER_ID_PATTERNS.PRODUCT_TYPE);
    if (productTypeMatch) {
      return {
        productType: value
      };
    }

    // Product vendor filter
    const productVendorMatch = param.match(FILTER_ID_PATTERNS.PRODUCT_VENDOR);
    if (productVendorMatch) {
      return {
        productVendor: value
      };
    }

    // Product metafield filter
    const productMetafieldMatch = param.match(FILTER_ID_PATTERNS.PRODUCT_METAFIELD);
    if (productMetafieldMatch) {
      const parts = productMetafieldMatch[1].split('.');

      return {
        productMetafield: {
          namespace: parts[0],
          key: parts[1],
          value: value
        }
      };
    }

    // Variant availability filter
    const variantAvailabilityMatch = param.match(FILTER_ID_PATTERNS.VARIANT_AVAILABILITY);
    if (variantAvailabilityMatch) {
      return {
        available: value
      };
    }

    // Variant option filter
    const variantOptionMatch = param.match(FILTER_ID_PATTERNS.VARIANT_OPTION);
    if (variantOptionMatch) {
      return {
        variantOption: {
          name: label,
          value: value
        }
      };
    }

    // Variant price filter

    // Variant taxonomy filter
    const variantTaxonomyMatch = param.match(FILTER_ID_PATTERNS.VARIANT_TAXONOMY);
    if (variantTaxonomyMatch) {
      const parts = variantTaxonomyMatch[1].split('.');
      return {
        taxonomyMetafield: {
          namespace: parts[0],
          key: parts[1],
          value: value
        }
      };
    }

    // Variant metafield filter
    const variantMetafieldMatch = param.match(FILTER_ID_PATTERNS.VARIANT_METAFIELD);
    if (variantMetafieldMatch) {
      const parts = variantMetafieldMatch[1].split('.');
      return {
        variantMetafield: {
          namespace: parts[0],
          key: parts[1],
          value: value
        }
      };
    }
  },

  formatMetafieldIdentifiers(identifiers) {
    if (!Array.isArray(identifiers)) return '';
    return identifiers
      .filter((i) => i && i.namespace && i.key)
      .map(
        (i) =>
          `{ namespace: "${String(i.namespace).replace(/"/g, '')}", key: "${String(i.key).replace(/"/g, '')}" }`
      )
      .join(', ');
  },

  getRelatedProductsQuery(tags) {
    const tagQuery = tags.map((tag) => `tag:'${tag}'`).join(' OR ');
    return `
    query getRelatedProducts {
      products(first: 10, query: "(${tagQuery})") {
        edges {
          node {
            id
            title
            handle
            images(first: 10) {
              edges {
                node {
                  altText
                  src
                }
              }
            }
          }
        }
      }
    }
  `;
  },
  // Helper function to generate the GraphQL query
  getRelatedProductsByHandleQuery(handles) {
    const handlesString = handles.map((handle) => `'${handle}'`).join(' OR ');
    const countryCode = window.store_country_code || 'AU';
    return `
        query GetRelatedProductsByHandle @inContext(country: ${countryCode}) {
            products(first: 20, query: "(${handlesString})") {
                edges {
                    node {
                        id
                        title
                        handle
                        tags

                        priceRange {
                            maxVariantPrice {
                              amount
                            }
                            minVariantPrice {
                              amount
                            }
                        }
                        variants(first: 100) {
                            edges {
                              node {
                                id
                                availableForSale
                                selectedOptions {
                                  value
                                }
                                compareAtPrice {
                                  amount
                                }
                                image {
                                  altText
                                  url
                                }
                              }
                            }
                          }
                        images(first: 10) {
                            edges {
                                node {
                                    src
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    `;
  },

  // Helper function to generate the GraphQL query for a single product
  getSingleProductByHandleQuery(handle) {
    const countryCode = window.store_country_code || 'AU';
    return `
        query GetProductByHandle @inContext(country: ${countryCode}) {
            product(handle: "${handle}") {
                id
                title
                handle
                tags
                vendor
                priceRange {
                    maxVariantPrice {
                      amount
                    }
                    minVariantPrice {
                      amount
                    }
                }
                variants(first: 100) {
                    edges {
                      node {
                        id
                        availableForSale
                        selectedOptions {
                          value
                        }
                        compareAtPrice {
                          amount
                        }
                        image {
                          altText
                          url
                        }
                      }
                    }
                  }
                images(first: 10) {
                    edges {
                        node {
                            src
                            url
                        }
                    }
                }
                metafields(identifiers: [${identifiersString}]) {
                  namespace
                  key
                  value
                  type
                  description
                  reference {
                    ... on Metaobject {
                      id
                      handle
                      field(key: "products") {
                        references(first: 20) {
                          edges {
                            node {
                              ... on Product {
                                id
                                title
                                handle
                                colourName: metafield(namespace: "info", key: "colour_name") {
                                    value
                                }
                                colourHex: metafield(namespace: "info", key: "colour_hex") {
                                    value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
            }
        }
    `;
  },

  async getMetafieldsForProduct(ids, metafieldIdentifiers = null) {
    // Ensure IDs are in proper GraphQL format
    const formattedIds = ids.map(id =>
      id.toString().startsWith('gid://shopify/Product/')
        ? id
        : `gid://shopify/Product/${id}`
    );

    // Use custom metafield identifiers or default ones
    const identifiers = metafieldIdentifiers || [
      { namespace: "custom", key: "bottle_siblings" },
      { namespace: "custom", key: "bottle_colour" },
      { namespace: "custom", key: "bottle_colour_name" }
    ];

    const query = `
        query getProductMetafields($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              handle
              metafields(identifiers: $identifiers) {
                namespace
                key
                value
                type
                description
              }
            }
          }
        }
      `;

    // Construct headers for the Storefront API
    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': `${api.getStorefrontToken()}`
    };

    try {
      // Send the request
      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          query: query,
          variables: {
            ids: formattedIds,
            identifiers: identifiers
          }
        })
      });

      const jsonResponse = await response.json();

      // Check for GraphQL errors
      if (jsonResponse.errors) {
        console.error('GraphQL errors:', jsonResponse.errors);
        throw new Error('GraphQL query failed');
      }

      // Extract products
      const products = jsonResponse.data?.nodes || [];

      // Transform the products as needed
      const transformedProducts = products.map((product) => this.transformGraphqlProduct(product));

      return transformedProducts;
    } catch (error) {
      console.error('Error fetching metafields:', error);
      throw error;
    }
  },
  /**
   * Enrich a list of transformed SearchSpring products with Shopify metafields.
   * Fetches metafields for all product IDs in a single query, then merges the
   * metafield data onto the matching products by ID.
   *
   * @param {Array} products - Array of transformed products (must have numeric `id`)
   * @returns {Promise<Array>} - Same array with `metafields` populated
   */
  async enrichSearchResultsWithMetafields(products) {
    if (!Array.isArray(products) || products.length === 0) return products;

    const identifiers = (window.productMetafieldIdentifiers || []).filter(id => !id.metaobjectField);
    if (identifiers.length === 0) return products;

    const ids = products
      .map(p => p.id)
      .filter(Boolean)
      .map(id => `gid://shopify/Product/${id}`);

    if (ids.length === 0) return products;

    const query = `
      query enrichProductMetafields($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            metafields(identifiers: $identifiers) {
              namespace
              key
              value
              type
            }
          }
        }
      }
    `;

    try {
      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
        },
        body: JSON.stringify({ query, variables: { ids, identifiers } })
      });

      const jsonResponse = await response.json();

      if (jsonResponse.errors) {
        console.warn('[shopify] enrichSearchResultsWithMetafields: GraphQL errors', jsonResponse.errors);
        return products;
      }

      // Build a map of numericId → metafields array
      const metafieldsByNumericId = new Map();
      for (const node of (jsonResponse.data?.nodes || [])) {
        if (!node?.id) continue;
        const numericId = parseInt(node.id.replace('gid://shopify/Product/', ''), 10);
        metafieldsByNumericId.set(numericId, node.metafields || []);
      }

      // Merge metafields onto matching products
      return products.map(product => {
        const metafields = metafieldsByNumericId.get(product.id);
        if (!metafields) return product;
        return {
          ...product,
          metafields: metafields
            .filter(Boolean)
            .map(metafield => {
              let parsedValue;
              try {
                const valueStr = String(metafield.value || '');
                if (valueStr.includes('[')) {
                  const parsed = JSON.parse(valueStr);
                  parsedValue = Array.isArray(parsed) ? parsed.flat() : [parsed];
                } else {
                  parsedValue = [metafield.value];
                }
              } catch {
                parsedValue = [metafield.value];
              }
              return { ...metafield, value: parsedValue };
            })
        };
      });
    } catch (error) {
      console.error('[shopify] enrichSearchResultsWithMetafields failed:', error);
      return products;
    }
  },

  async authenticatedFetch(fetchDetails) {
    fetchDetails.url = `${helpers.getDomain()}/admin/api/2025-01/graphql.json`;
    fetchDetails.shop_url = `${helpers.getDomain(false, true)}/admin/api/2025-01/graphql.json`;
    fetchDetails.key = 'SHOPIFY_API_AUTHENTICATED_KEY';
    // fetchDetails.hash = sig;

    const response = await fetch(`${helpers.getDomain()}/apps/arctheme/fetch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fetchDetails)
    });

    if (!response.ok) {
      console.error(`Error calling authenticated api: ${response.statusText}`);
    }
    return await response.json();
  },
  
  async unauthenticatedFetch(query, variables = null) {
    // Construct the URL based on the 'storefront' flag
    const apiPath = storefront ? 'api' : 'admin/api';
    const url = `${helpers.getDomain()}/${apiPath}/2025-01/graphql.json`;

    // Construct headers conditionally based on the 'storefront' flag
    let headers = {
      'Content-Type': 'application/graphql'
    };

    if (storefront) {
      headers['X-Shopify-Storefront-Access-Token'] = `${api.getStorefrontToken()}`; // This key to be stored elsewhere
    } else {
      headers['X-Shopify-Access-Token'] = `${api.getPublicAccessToken()}`; // This key to be stored elsewhere (Can only be private app key, cannot be the public/custom app key)
    }

    // Perform the GraphQL request
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: query
    });

    return await response.json();
  },

  async getStockForProducts(productData) {
    const data = {
      query: GET_PRODUCTS_QUERY.loc.source.body,
      variables: {
        ids: productData.reduce((filtered, id) => {
          filtered.push(id.toString().startsWith('gid://shopify/Product/') ? id : 'gid://shopify/Product/' + id);
          return filtered;
        }, [])
      }
    };

    let headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };

    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data)
    });
    
    const jsonResponse = await response.json();
    // const products = jsonResponse.data?.nodes.filter((node) => node);
    const products = jsonResponse.data?.nodes
      .filter((node) => node)
      .map((product) => this.transformGraphqlProduct(product));

    return products;
  },

  async getShopifyArticleList(tags) {
    const tagQuery = tags.map((tag) => `tag:'${tag}'`).join(' OR ');
    const data = `{
        articles(first: 10, query: "(${tagQuery})") {
            edges {
                node {
                    id
                    title
                    tags
                    excerpt
                    publishedAt
                    handle
                    blog {
                        handle
                    }
                    image {
                        src
                        altText
                    }
                }
            }
        }
    }`;

    let headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };
    
    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: data
    });

    const jsonResponse = await response.json();
    const articles = jsonResponse.data?.articles.edges.map((edge) => edge.node);

    if (window.enableDebugMode) { console.log(articles); }

    const transformedArticles = articles.map((article) => this.transformGraphqlArticle(article));

    return transformedArticles;
  },

  transformGraphqlArticle(article) {
    return {
      id: article.id,
      title: article.title,
      url: article.url,
      handle: article.handle,
      tags: article.tags,
      excerpt: article.excerpt,
      publishedAt: article.publishedAt,
      blog: article.blog
        ? {
            handle: article.blog.handle
          }
        : null,
      image: article.image
        ? {
            src: article.image.src,
            altText: article.image.altText
          }
        : null
    };
  },

  async getShopifyProductList(productData) {
    const metafieldIdentifiers = (window.productMetafieldIdentifiers || []).filter(id => !id.metaobjectField);
    const hasMetafields = metafieldIdentifiers.length > 0;

    // Build inline metaobject alias fields
    const metaobjectFragment = buildMetaobjectFragment();

    const query = `
      query getProducts(
        $ids: [ID!]!
        ${hasMetafields ? '$metafieldIdentifiers: [HasMetafieldsIdentifier!]!' : ''}
      ) {
        nodes(ids: $ids) {
          id
          ... on Product {
            title
            handle
            tags
            productType
            createdAt
            totalInventory
            vendor
            options {
              name
              values
            }
            featuredImage {
              altText
              url
            }
            isGiftCard
            images(first: 10) {
              edges {
                node {
                  altText
                  url
                }
              }
            }
            media(first: 5) {
              edges {
                node {
                  alt
                  mediaContentType
                  ... on Video {
                    id
                    sources {
                      url
                      width
                      height
                      format
                    }
                  }
                }
              }
            }
            ${hasMetafields ? `
            metafields(identifiers: $metafieldIdentifiers) {
              namespace
              key
              value
              type
              description
            }
            ` : ''}
            ${metaobjectFragment}
            sellingPlanGroups(first: 5) {
              edges {
                node {
                  name
                  options {
                    name
                    values
                  }
                  sellingPlans(first: 5) {
                    edges {
                      node {
                        id
                        name
                        description
                        priceAdjustments {
                          adjustmentValue {
                            ... on SellingPlanFixedPriceAdjustment {
                              price {
                                amount
                                currencyCode
                              }
                            }
                            ... on SellingPlanPercentagePriceAdjustment {
                              adjustmentPercentage
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            priceRange {
              maxVariantPrice {
                amount
                currencyCode
              }
              minVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  availableForSale
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  selectedOptions {
                    name
                    value
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  image {
                    altText
                    url
                  }
                }
              }
            }
          }
        }
      }
    `;

    const ids = productData.reduce((filtered, id) => {
      filtered.push(id.toString().startsWith('gid://shopify/Product/') ? id : 'gid://shopify/Product/' + id);
      return filtered;
    }, []);

    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };

    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        variables: {
          ids,
          ...(hasMetafields && { metafieldIdentifiers })
        }
      })
    });

    const jsonResponse = await response.json();

    const products = jsonResponse.data?.nodes
      .filter((node) => node)
      .map((product) => this.transformGraphqlProduct(product));

    return products;
  },

  async getRelatedProducts(products, checkTag = 'colour:') {
    const tags = products.flatMap((product) => product.tags.filter((tag) => tag.startsWith(checkTag)));
    const uniqueTags = [...new Set(tags)];
    const relatedProductsQuery = this.getRelatedProductsQuery(uniqueTags);

    let headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };

    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: relatedProductsQuery
    });

    const jsonResponse = await response.json();

    const relatedProducts = jsonResponse.data?.products?.edges.map((edge) => edge.node);
    return relatedProducts || [];
  },

  async getProductsByHandle(product_handles) {
    // Extract handles from tags that start with the given prefix
    if (product_handles.length > 0) {
      const relatedProductsQuery = this.getRelatedProductsByHandleQuery(product_handles);

      // console.log('Handle Products', relatedProductsQuery);

      let headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
      };

      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: relatedProductsQuery
      });

      const jsonResponse = await response.json();

      const relatedProducts = jsonResponse.data?.products?.edges.map((edge) => edge.node);
      const transformedRelatedProducts = relatedProducts?.map((product) => this.transformGraphqlProduct(product));
      return transformedRelatedProducts || [];
    }
    return [];
  },

  /**
   * Fetch products for a given collection handle using Storefront API
   * @param {string} collectionHandle - The handle of the collection
   * @returns {Promise<Array>} Array of product objects
   */
  async getProductsForCollection(collectionHandle, limit = 10) {
    if (!collectionHandle) return [];
    const query = `
      query getProductsForCollection($handle: String!, $limit: Int!) @inContext(country: ${window.store_country_code || 'AU'}) {
        collectionByHandle(handle: $handle) {
          products(first: $limit) {
            edges {
              node {
                ...ProductFragment
              }
            }
          }
        }
      }
      ${PRODUCT_FRAGMENT.loc.source.body}
    `;
    const variables = {
      handle: collectionHandle,
      limit
    };
    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };
    const endpoint = api.getStorefrontGraphqlBaseUrl();
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables })
      });
      const json = await response.json();
      const edges = json?.data?.collectionByHandle?.products?.edges || [];
      return edges.map(edge => this.transformGraphqlProduct(edge.node));
    } catch (error) {
      console.error('Error fetching products for collection:', error);
      return [];
    }
  },

  async getProductByHandle(product_handle) {
    // Fetch a single product by handle
    if (product_handle) {
      const singleProductQuery = this.getSingleProductByHandleQuery(product_handle);

      let headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
      };

      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: singleProductQuery
      });

      const jsonResponse = await response.json();

      const product = jsonResponse.data?.product;
      const transformedProduct = product ? this.transformGraphqlProduct(product) : null;
      
      return transformedProduct;
    }
    return null;
  },

  async getRelatedProductsByTag(product, tagPrefix = 'colour:') {
    // Extract handles from tags that start with the given prefix
    const tags = product.tags
      .filter((tag) => tag.startsWith(tagPrefix))
      .map((tag) => tag.replace(tagPrefix, '').trim());

    const uniqueTags = [...new Set(tags)]; // Get unique handles

    if (uniqueTags.length > 0) {
      const relatedProductsQuery = this.getRelatedProductsByHandleQuery(uniqueTags);

      let headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
      };

      const endpoint = api.getStorefrontGraphqlBaseUrl();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: relatedProductsQuery
      });

      const jsonResponse = await response.json();

      const relatedProducts = jsonResponse.data?.products?.edges.map((edge) => edge.node);
      const transformedRelatedProducts = relatedProducts?.map((product) => this.transformGraphqlProduct(product));
      return transformedRelatedProducts || [];
    }
    return [];
  },

  async getRecommendedProducts(productId) {
    try {
      let headers = {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': `${api.getStorefrontToken()}`
      };

      const response = await fetch(`${helpers.getDomain()}/recommendations/products.json?product_id=${productId}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Error fetching recommendations: ${response.statusText}`);
      }

      const jsonResponse = await response.json();

      // console.log("Recommended response:", jsonResponse);
      return jsonResponse.products || [];
    } catch (error) {
      console.error('Failed to fetch product recommendations:', error);
    }
  },

  transformPrice(price) {
    return price / 100;
  },

  /**
   * Extract sibling product nodes from a swatchSource metafield.
   * Supports two source shapes:
   *   - 3-part (e.g. info.siblings.products): swatchSource.reference (Metaobject) → field → references.edges
   *   - 2-part (e.g. custom.siblings): swatchSource.references.edges (direct product list)
   */
  extractSwatchSiblings(sibling) {
    if (!sibling) return [];

    // 3-part: metafield → Metaobject → field(key) → Product references
    const metaobjectFieldReferences = sibling.reference?.field?.references?.edges;
    if (Array.isArray(metaobjectFieldReferences)) {
      return metaobjectFieldReferences.map((edge) => edge.node).filter(Boolean);
    }

    // 2-part: metafield → direct Product list references
    const directReferences = sibling.references?.edges;
    if (Array.isArray(directReferences)) {
      return directReferences.map((edge) => edge.node).filter(Boolean);
    }

    return [];
  },

  /**
   * Read aliased metaobject-ref fields from a GraphQL product node (placed there by
   * buildMetaobjectRefFragment) and return them as normalised metafield objects whose
   * value is the resolved scalar from the Metaobject field.
   *
   * @param {Object} productNode - Raw GraphQL product node
   * @returns {Array} Normalised metafield objects
   */
  normalizeMetaobjectRefFields(productNode) {
    const refs = (window.productMetafieldIdentifiers || []).filter(id => id.metaobjectField);
    return refs
      .map(({ namespace, key, metaobjectField }) => {
        const alias = `metaobj_${namespace}_${key}_${metaobjectField}`.replace(/[^a-zA-Z0-9_]/g, '_');
        const field = productNode[alias];
        if (!field) return null;
        const resolvedValue = field.reference?.field?.value ?? null;
        return {
          namespace,
          key,
          type: field.type || 'metaobject_reference',
          value: [resolvedValue],
          _metaobjectField: metaobjectField
        };
      })
      .filter(Boolean);
  },

  transformGraphqlProduct(productNode, collectionHandle = null) {
    const productVideo = productNode.media?.edges && Array.isArray(productNode.media.edges) 
      ? productNode.media.edges.filter((edge) => edge.node.mediaContentType === 'VIDEO')?.[0] 
      : null; // Filter nodes with mediaContentType "VIDEO"
    const hasOnlyDefaultVariant = (productNode?.options?.[0]?.values?.[0] ?? '') === 'Default Title';

    // Transform siblings metafield into a normalised siblings array.
    // Sibling nodes carry aliased fields: swatchValue, swatchLabel, swatchImage
    // as queried by buildSwatchFragment() in ShopifyProvider.
    const resolveSwatchField = (field) => {
      if (!field) return null;
      // Plain metafield value (namespace.key)
      if (field.value !== undefined) return field.value;
      // Metaobject subField (namespace.key.subField)
      if (field.reference?.field?.value !== undefined) return field.reference.field.value;
      return null;
    };

    const swatchSiblings = this.extractSwatchSiblings(productNode.siblings);
    const swatches = swatchSiblings.map((sibling) => {
      const swatchValue = resolveSwatchField(sibling.swatchValue);
      const swatchLabel = resolveSwatchField(sibling.swatchLabel) ?? swatchValue;
      // 2-part (namespace.key → MediaImage): reference.image.url
      // 3-part (namespace.key.subField → Metaobject field → MediaImage): reference.field.reference.image.url
      const swatchImage =
        sibling.swatchImage?.reference?.image?.url ??
        sibling.swatchImage?.reference?.field?.reference?.image?.url ??
        null;

      const siblingId = sibling.id ? parseInt(sibling.id.replace('gid://shopify/Product/', '')) : null;
      const siblingMedia = sibling.images?.edges
        ? sibling.images.edges.map(({ node: img }) => ({
            alt: img.altText || sibling.title,
            media_type: 'image',
            src: img.url,
            width: img.width || null,
            height: img.height || null
          }))
        : [];
      const siblingFeaturedImage = sibling.featuredImage
        ? {
            src: sibling.featuredImage.url ?? null,
            alt: sibling.featuredImage.altText ?? null,
            width: sibling.featuredImage.width ?? null,
            height: sibling.featuredImage.height ?? null,
            media_type: 'image'
          }
        : null;

      return {
        id: siblingId,
        url: `/products/${sibling.handle}`,
        title: sibling.title ?? null,
        handle: sibling.handle ?? null,
        featuredImage: siblingFeaturedImage,
        swatchValue: swatchValue,
        swatchLabel: swatchLabel,
        swatchImage: swatchImage,
        siblingProduct: siblingId ? {
          id: siblingId,
          objectType: 'product',
          title: sibling.title ?? null,
          handle: sibling.handle ?? null,
          url: `/products/${sibling.handle}`,
          tags: sibling.tags || [],
          available: sibling.variants?.edges?.some(e => e.node.availableForSale) || false,
          price: sibling.priceRange?.minVariantPrice?.amount
            ? Math.round(parseFloat(sibling.priceRange.minVariantPrice.amount) * 100)
            : null,
          compare_at_price: sibling.compareAtPriceRange?.minVariantPrice?.amount
            ? Math.round(parseFloat(sibling.compareAtPriceRange.minVariantPrice.amount) * 100)
            : null,
          featuredImage: siblingFeaturedImage,
          media: siblingMedia,
          variants: sibling.variants?.edges
            ? sibling.variants.edges.map(({ node }) => ({
                id: parseInt(node.id.replace('gid://shopify/ProductVariant/', '')),
                available: node.availableForSale,
                sku: node.sku,
                compare_at_price: node.compareAtPrice?.amount ? node.compareAtPrice.amount * 100 : null,
                options: node.selectedOptions?.map(o => o.value) || [],
                image: {
                  alt: node.image?.altText || sibling.title,
                  media_type: 'image',
                  src: node.image?.url || null
                }
              }))
            : [],
          swatches: []
        } : null
      };
    });

    return productNode
      ? {
          ...productNode,
          objectType: 'product',
          url: collectionHandle
            ? `/collections/${collectionHandle}/products/${productNode.handle}`
            : `/products/${productNode.handle}`,
          id: parseInt(productNode.id.replace('gid://shopify/Product/', '')),
          tags: productNode.tags && Array.isArray(productNode.tags) ? productNode.tags.map((tag) => tag) : [],
          // collections data for category extraction
          collections:
            productNode.collections?.edges && Array.isArray(productNode.collections.edges)
              ? productNode.collections.edges.map(({ node }) => ({
                  id: parseInt(node.id.replace('gid://shopify/Collection/', '')),
                  title: node.title,
                  handle: node.handle
                }))
              : [],
          swatches,
          // metafield values are transformed into arrays for consistency
          // reference/references data is passed through when present (e.g. metaobject references)
          metafields: [
            ...(productNode.metafields && Array.isArray(productNode.metafields)
              ? productNode.metafields
                  .filter((node) => node)
                  .map((metafield) => {
                  let parsedValue;
                  try {
                    // Ensure metafield.value is a string before checking for JSON array
                    const valueStr = String(metafield.value || '');
                    if (valueStr.includes('[')) {
                      const parsed = JSON.parse(valueStr);
                      parsedValue = Array.isArray(parsed) ? parsed.flat() : [parsed];
                    } else {
                      parsedValue = [metafield.value];
                    }
                  } catch {
                    parsedValue = [metafield.value];
                  }

                  return {
                    ...metafield,
                    value: parsedValue,
                    // Pass through reference data for single-reference metafields
                    ...(metafield.reference !== undefined && { reference: metafield.reference }),
                    // Pass through references data for list-reference metafields
                    ...(metafield.references !== undefined && {
                      references: metafield.references?.edges
                        ? metafield.references.edges.map((edge) => edge.node).filter(Boolean)
                        : metafield.references
                    })
                  };
                })
              : []),
            // Merge in metaobject-ref metafields resolved from aliased fields
            ...this.normalizeMetaobjectRefFields(productNode)
          ],
          variants: productNode.variants?.edges && Array.isArray(productNode.variants.edges) 
            ? productNode.variants.edges.map(({ node }) => {
                return {
                  id: parseInt(node.id.replace('gid://shopify/ProductVariant/', '')),
                  title: node.title,
                  available: node.availableForSale,
                  sku: node.sku,
                  compare_at_price: node.compareAtPrice?.amount * 100 || null,
                  options: node.selectedOptions && Array.isArray(node.selectedOptions) 
                    ? node.selectedOptions.map((option) => option.value) 
                    : [],
                  image: {
                    alt: node?.image?.altText ? node.image.altText : productNode.title,
                    media_type: 'image',
                    src: node?.image?.url ? node.image.url : null
                  }
                };
              })
            : [],
          selling_plan_groups: productNode.sellingPlanGroups?.edges && Array.isArray(productNode.sellingPlanGroups.edges)
            ? productNode.sellingPlanGroups.edges.map(({ node }) => {
                return {
                  name: node.name,
                  options: node.options && Array.isArray(node.options) ? node.options.map((option) => option) : [],
                  selling_plans: node.sellingPlans?.edges && Array.isArray(node.sellingPlans.edges) 
                    ? node.sellingPlans.edges.map(({ node }) => {
                        return {
                          id: parseInt(node.id.replace('gid://shopify/SellingPlan/', '')),
                          name: node.name,
                          description: node.description,
                          price_adjustments: node.priceAdjustments && Array.isArray(node.priceAdjustments)
                            ? node.priceAdjustments.map(({ adjustmentValue }) => {
                                return {
                                  value_type: 'percentage',
                                  value: adjustmentValue.adjustmentPercentage
                                };
                              })
                            : []
                        };
                      })
                    : []
                };
              })
            : [],
          price_calculated: productNode.priceRange?.minVariantPrice.amount,
          compare_at_price_calculated: productNode.variants?.edges[0].node.compareAtPrice?.amount || null,
          price: productNode.priceRange?.minVariantPrice.amount * 100,
          compare_at_price: productNode.variants?.edges[0].node.compareAtPrice?.amount * 100 || null,
          featuredImage: {
            alt: productNode?.featuredImage?.altText ? productNode.featuredImage.altText : productNode.title,
            media_type: 'image',
            src: productNode?.featuredImage?.url ? productNode.featuredImage.url : null
          },
          media: [
            ...(productNode.images?.edges && Array.isArray(productNode.images.edges)
              ? productNode.images.edges.map((image) => {
                  return {
                    alt: image?.node?.altText ? image.node.altText : productNode.title,
                    media_type: 'image',
                    src: image?.node?.url ? image.node.url : null,
                    width: image?.node?.width || null,
                    height: image?.node?.height || null
                  };
                })
              : []),
            ...(productVideo
              ? [
                  {
                    alt: `${productNode.title}_featured-video`,
                    media_type: 'video',
                    sources: productVideo.node?.sources && Array.isArray(productVideo.node.sources)
                      ? productVideo.node.sources.filter((source) => source.format === 'mp4')
                      : []
                  }
                ]
              : [])
          ],
          has_only_default_variant: hasOnlyDefaultVariant,
          options: productNode.options && Array.isArray(productNode.options) 
            ? productNode.options.map((option) => option.name) 
            : [],
          options_with_values: productNode.options && Array.isArray(productNode.options) 
            ? productNode.options.map((option) => {
                return {
                  name: option.name,
                  position: productNode.options.indexOf(option) + 1,
                  values: option.values && Array.isArray(option.values) ? option.values : []
                };
              })
            : []
        }
      : null;
  },

  getShopifyImageUrl(originalUrl, width = null, height = null, customQueries = null) {
    const params = {
      width: width ? Math.ceil(width) : null,
      height: height ? Math.ceil(height) : null,
      ...customQueries
    };

    const url = helpers.updateUrlParameters(originalUrl, params);
    return url;
  },

  // Convert Shopify richtext schema into HTML
  // Refer to https://github.com/TheBeyondGroup/shopify-rich-text-renderer#readme for more details
  renderRichText(schema, options) {
    return convertSchemaToHtml(schema, options);
  },

  // Decoding escaped string - Use for escaped HTML (ex. collection description)
  decodeLiquidEscape(input) {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent || '';
  },

  // Decoding and parsing escaped JSON string - Use for escaped object (ex. richtext)
  decodeAndParseLiquidEscapeObject(input) {
    return JSON.parse(this.decodeLiquidEscape(input));
  },

  getVideoSrc(video) {
    return video?.sources.find((source) => source.format === 'mp4');
  },

  interpolateLocale(str, data) {
    let result = str;
    const variables = Object.keys(data);
    variables.forEach((variable) => {
      result = result.replace(`{{ ${variable} }}`, data[variable]);
    });

    return result;
  },

  getShopifyFileUrl(handle, fileType) {
    return window.fileUrl.split('?')[0].replace('replace', `${handle}.${fileType}`);
  },

  async getCountries() {
    const data = await addressFormatter.getCountries();

    return data;
  },

  async getProvinces(countryCode) {
    const data = await addressFormatter.getCountry(countryCode);

    return data.zones;
  },

  async getCollectionsByHandles(collectionHandles) {
    if (collectionHandles.length === 0) return [];

    const query = `{
      ${collectionHandles.map(handle => `
        ${handle.replaceAll('-', '_')}: collectionByHandle(handle: "${handle}") {
          title
          handle
          metafield (namespace: "custom", key: "header_search_image") {
            reference {
              ... on MediaImage {
                image {
                  src
                  altText
                  width
                  height
                }
              }
            }
          }
          image {
            src
            altText
            width
            height
          }
        }
      `).join('')}
    }`;
    // console.log('getCollectionsByHandles query', query);

    let headers = {
      'Content-Type': 'application/graphql',
      'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
    };

    const endpoint = api.getStorefrontGraphqlBaseUrl();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: query
    });

    const jsonResponse = await response.json();
    // console.log('response', jsonResponse);

    return jsonResponse.data ? Object.values(jsonResponse.data) : [];
  }
};